import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ModelBase } from 'store/model-base';
import { StoreAction } from 'store/actions';
import { ApiChannel } from 'core/constants';
import { buildHeaders, XmStore } from 'core/services';

import { GtpSessionCollection, RelatedStatementTransactionCollection, SsmEstimated, SsmHistorical, SsmStatementList, SsmStatementListV2, Statement, StatementList, StatementV2Pdf } from './models';
import { StatementPdf } from './models/statement-pdf';

@Injectable()
export class StatementApi {
    public static getEstimated(_xmStore: XmStore, http: HttpClient, _params: ApiParams): Observable<Statement> {
        return http.get('/accounts/statements/estimated', {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: Statement) => Statement.create<Statement>(response))
        );
    }

    public static getSSMEstimated(_xmStore: XmStore, http: HttpClient, _params: ApiParams): Observable<SsmEstimated> {
        return http.get('/xm/estimated', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: SsmEstimated) => SsmEstimated.create<SsmEstimated>(response))
        );
    }

    public static getStatement(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<Statement> {
        return http.get(`/accounts/statements/${params['id.key']}`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: Statement) => Statement.create<Statement>(response, params['id.key']))
        );
    }

    public static getSSMHistoricalStatement(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<SsmHistorical> {
        return http.get('/xm/historicalBill', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY }),
            params: { docId: params['id.key'] }
        }).pipe(
            map((response: Statement) => SsmHistorical.create<SsmHistorical>(response, params['id.key']))
        );
    }

    public static getSSMHistoricalV2Statement(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<SsmHistorical> {
        return http.get('/xm/historicalBill-v2', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY }),
            params: { docId: params['id.key'] }
        }).pipe(
            map((response: Statement) => SsmHistorical.create<SsmHistorical>(response, params['id.key']))
        );
    }

    public static getStatementList(_xmStore: XmStore, http: HttpClient, _params: ApiParams): Observable<StatementList> {
        return http.get('/account/statements', {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => StatementList.create<StatementList>({ statements: response }))
        );
    }

    public static getSsmStatementList(_xmStore: XmStore, http: HttpClient, _params: ApiParams): Observable<SsmStatementList> {
        return http.get('/xm/statementsList', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) => SsmStatementList.create<SsmStatementList>(response))
        );
    }

    public static getSsmStatementListV2(_xmStore: XmStore, http: HttpClient, _params: ApiParams): Observable<SsmStatementListV2> {
        return http.get('/xm/statementsList-v2', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) => SsmStatementListV2.create<SsmStatementListV2>(response))
        );
    }

    public static getRelatedStatementTransaction(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RelatedStatementTransactionCollection> {
        return http.get(`/accounts/statements/${params.statement_id}/transactions`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RelatedStatementTransactionCollection.create<RelatedStatementTransactionCollection>({ relatedStatementTransaction: response, statementKey: params.statement_id }))
        );
    }

    public static getGtpSessions(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<GtpSessionCollection> {
        return http.get(`/account/statements/${params.statement_id}/gtpsessions`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => GtpSessionCollection.create<GtpSessionCollection>({ gtpSessionCollection: response, statementKey: params.statement_id }))
        );
    }

    public static getStatementPdf(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<StatementPdf> {  
        return http.get(`/xm/statement/pdf?docId=${params.docId}&signed=true`, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) => StatementPdf.create<StatementPdf>(response))
        );
    }

    public static getStatementV2Pdf(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<StatementV2Pdf> {  
        return http.get(`/xm/statement-v2/pdf?docId=${params.docId}&source=${params.source}&signed=true`, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) => StatementV2Pdf.create<StatementV2Pdf>(response))
        );
    }
}

ModelBase.fetchMapping[StoreAction.GET_ESTIMATED] = StatementApi.getEstimated;
ModelBase.fetchMapping[StoreAction.GET_SSM_ESTIMATED] = StatementApi.getSSMEstimated;
ModelBase.fetchMapping[StoreAction.GET_STATEMENT] = StatementApi.getStatement;
ModelBase.fetchMapping[StoreAction.GET_SSM_HISTORICAL_STATEMENT] = StatementApi.getSSMHistoricalStatement;
ModelBase.fetchMapping[StoreAction.GET_STATEMENT_LIST] = StatementApi.getStatementList;
ModelBase.fetchMapping[StoreAction.GET_SSM_STATEMENT_LIST] = StatementApi.getSsmStatementList;
ModelBase.fetchMapping[StoreAction.GET_SSM_STATEMENT_LIST_V2] = StatementApi.getSsmStatementListV2;
ModelBase.fetchMapping[StoreAction.GET_RELATED_STATEMENT_TRANSACTIONS] = StatementApi.getRelatedStatementTransaction;
ModelBase.fetchMapping[StoreAction.GET_GTP_SESSION_COLLECTION] = StatementApi.getGtpSessions;
ModelBase.fetchMapping[StoreAction.GET_STATEMENT_PDF] = StatementApi.getStatementPdf;
ModelBase.fetchMapping[StoreAction.GET_STATEMENT_V2_PDF] = StatementApi.getStatementV2Pdf;
ModelBase.fetchMapping[StoreAction.GET_SSM_HISTORICAL_STATEMENT_V2] = StatementApi.getSSMHistoricalV2Statement;

